import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useGetProfile, useUpdateProfile } from '@biasafe.queries/user/user';
import type { CornerStyle, Theme } from '@/shared/types';
import { usePreferencesStore } from '@/entities/preferencesStore';

/**
 * Appearance follows the ACCOUNT, with localStorage demoted to a cache.
 *
 * WHY THE CACHE CANNOT SIMPLY GO AWAY. `index.html` reads
 * `localStorage['app-preferences']` in an inline script before the bundle
 * loads, so the first paint is already in the right theme. A server value
 * arrives after `GET /user` resolves, which is several hundred milliseconds
 * too late — the flash that bootstrap exists to prevent. So the server is the
 * truth and the local copy is what the next first paint reads.
 *
 * THE PRECEDENCE, and the one case it is chosen for:
 *
 *   - the account has a value  -> it wins, and is written into the cache
 *   - the account has null     -> the person has never chosen; the local
 *                                 value stands and is pushed up, so an
 *                                 existing user's setting is adopted by their
 *                                 account rather than being reset by it
 *   - the user changes it      -> store first (instant), then PUT
 *
 * Null therefore has to mean "never chose" rather than "chose the default",
 * which is why the column is nullable and the migration backfills nothing. If
 * it defaulted to 'system' server-side, every existing user's saved choice
 * would be overwritten by a default the moment this shipped.
 *
 * ON SIGN-OUT THE CACHE IS CLEARED, in `resetStores`. That is the direct cost
 * of making this per-account: two people sharing a machine must not inherit
 * each other's theme through a cache that outlives the session. It buys one
 * frame of default appearance immediately after a fresh sign-in, before the
 * profile resolves, which is the cheaper of the two wrongs.
 */
export function useAccountAppearance(): void {
  const { data: profile } = useGetProfile();
  const { mutate: save } = useUpdateProfile({
    mutation: {
      // The picker promises "saved to your account". Without this a failed PUT
      // left the new look applied on this device only, with nothing said, and
      // the next profile load put the old one back.
      onError: () =>
        toast.error(
          'Could not save your appearance to your account. It is applied on this device only.',
        ),
    },
  });

  const theme = usePreferencesStore((s) => s.theme);
  const cornerStyle = usePreferencesStore((s) => s.cornerStyle);
  const setTheme = usePreferencesStore((s) => s.setTheme);
  const setCornerStyle = usePreferencesStore((s) => s.setCornerStyle);

  /**
   * What the account last told us, so a write is only sent when the value
   * actually differs. Without it the effect below would PUT its own hydration
   * straight back and every mount would cost a write.
   */
  const fromAccount = useRef<{ theme?: string; corner?: string }>({});
  const adopted = useRef(false);
  /**
   * Whether the "client -> account" effect has seen its first run.
   *
   * It has to skip that run, and the reason is an ordering detail worth
   * stating. Both effects fire in the same commit: the first sets
   * `fromAccount` and calls `setTheme`, but the second still sees the render's
   * OLD store value, so it compares 'system' against the account's 'dark',
   * decides the user changed something, and writes the pre-hydration value
   * straight back over the account.
   *
   * Skipping the mount costs nothing — a user cannot have changed a setting
   * before the component existed — and the re-render that hydration causes
   * runs this effect again with the values agreeing.
   */
  const userMayHaveChangedIt = useRef(false);

  // Account -> client.
  useEffect(() => {
    if (!profile) return;
    const accountTheme = profile.theme as Theme | null | undefined;
    const accountCorner = profile.cornerStyle as CornerStyle | null | undefined;

    if (accountTheme) {
      fromAccount.current.theme = accountTheme;
      if (accountTheme !== theme) setTheme(accountTheme);
    }
    if (accountCorner) {
      fromAccount.current.corner = accountCorner;
      if (accountCorner !== cornerStyle) setCornerStyle(accountCorner);
    }

    // Adopt each unset preference independently. Accounts can have only one
    // saved value; if hydration leaves the store unchanged, the next effect
    // never runs and cannot persist the other half. Preserve the account's
    // existing choice while adopting only the missing browser preference.
    if (!adopted.current && (!accountTheme || !accountCorner)) {
      adopted.current = true;
      const adoptedTheme = accountTheme ?? theme;
      const adoptedCorner = accountCorner ?? cornerStyle;
      fromAccount.current = { theme: adoptedTheme, corner: adoptedCorner };
      save({ data: { theme: adoptedTheme, cornerStyle: adoptedCorner } });
    }
    // `theme`/`cornerStyle` are deliberately absent: this effect reacts to the
    // PROFILE arriving, not to the user changing a setting — that is the next
    // effect's job, and including them here would make the two fight.
  }, [profile]);

  // Client -> account.
  useEffect(() => {
    if (!userMayHaveChangedIt.current) {
      userMayHaveChangedIt.current = true;
      return;
    }
    if (!profile) return;
    if (
      fromAccount.current.theme === theme &&
      fromAccount.current.corner === cornerStyle
    ) {
      return;
    }
    fromAccount.current = { theme, corner: cornerStyle };
    save({ data: { theme, cornerStyle } });
  }, [theme, cornerStyle]);
}
